import Swal from "sweetalert2";

// ---------- Inline SVG icons (no extra deps) ----------
const iconCheck = `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px;"><path d="M3 8.5l3.5 3.5L13 4.5"/></svg>`;
const iconX = `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px;"><path d="M4 4l8 8M12 4l-8 8"/></svg>`;
const iconExclamation = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:24px;height:24px;"><path d="M12 9v4M12 17h0M5 19h14a2 2 0 0 0 1.7-3l-7-12a2 2 0 0 0-3.4 0l-7 12A2 2 0 0 0 5 19z"/></svg>`;
const iconShield = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:22px;height:22px;"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>`;

// ---------- One-time CSS injection for things customClass can't reach ----------
let stylesInjected = false;
const ensureStyles = () => {
  if (stylesInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.id = "khazma-swal-styles";
  style.textContent = `
    /* Toast: full-bleed, no padding, refined shadow */
    .khazma-swal-toast {
      padding: 0 !important;
      width: auto !important;
      min-width: 300px !important;
      max-width: 420px !important;
      border-radius: 16px !important;
      overflow: hidden !important;
      box-shadow: 0 20px 50px -10px rgba(0,0,0,0.16), 0 8px 20px -6px rgba(0,0,0,0.08) !important;
      background: transparent !important;
      border: none !important;
    }
    .khazma-swal-toast .swal2-html-container {
      padding: 0 !important;
      margin: 0 !important;
      font-size: 14px !important;
      line-height: 1.5 !important;
      text-align: start !important;
      overflow: visible !important;
    }
    .khazma-swal-toast .swal2-timer-progress-bar-container {
      height: 3px !important;
      border-radius: 0 !important;
    }
    .khazma-swal-toast .swal2-timer-progress-bar {
      border-radius: 0 !important;
    }

    /* Confirm dialog: full custom layout */
    .khazma-swal-confirm {
      padding: 0 !important;
      border-radius: 20px !important;
      overflow: hidden !important;
      box-shadow: 0 30px 80px -15px rgba(15,23,42,0.25), 0 12px 24px -8px rgba(15,23,42,0.12) !important;
    }
    .khazma-swal-confirm .swal2-html-container {
      padding: 0 !important;
      margin: 0 !important;
      overflow: visible !important;
      text-align: start !important;
    }
    .khazma-swal-confirm .swal2-actions {
      padding: 16px 24px 24px !important;
      margin: 0 !important;
      gap: 8px !important;
      width: 100% !important;
      justify-content: flex-end !important;
    }
    .dark .khazma-swal-confirm { background: #161A1C !important; }
    .dark .khazma-swal-confirm .swal2-html-container { color: #6b7280 !important; }
    .dark .khazma-swal-text { color: #6b7280 !important; }

    /* OTP dialog */
    .khazma-swal-otp {
      border-radius: 20px !important;
      padding: 32px !important;
      box-shadow: 0 30px 80px -15px rgba(15,23,42,0.25), 0 12px 24px -8px rgba(15,23,42,0.12) !important;
    }
    .khazma-swal-otp .swal2-input {
      letter-spacing: 0.5em !important;
      font-variant-numeric: tabular-nums !important;
    }
 
  `;
  document.head.appendChild(style);
  stylesInjected = true;
};

// ---------- Tone presets ----------
const TONES = {
  success: {
    stripeFrom: "#34d399",
    stripeTo: "#059669",
    chipFrom: "#10b981",
    chipTo: "#047857",
    progressFrom: "#34d399",
    progressTo: "#059669",
  },
  danger: {
    stripeFrom: "#fb7185",
    stripeTo: "#e11d48",
    chipFrom: "#f43f5e",
    chipTo: "#be123c",
    progressFrom: "#fb7185",
    progressTo: "#e11d48",
  },
};

const buildToastHtml = (label, icon, tone) => `
  <div dir="rtl" style="position:relative;display:flex;align-items:stretch;background:#ffffff;color:#0f172a;">
    <span aria-hidden style="width:4px;flex-shrink:0;background:linear-gradient(to bottom, ${tone.stripeFrom}, ${tone.stripeTo});"></span>
    <div style="display:flex;flex:1;align-items:center;gap:12px;padding:14px 16px;">
      <span style="display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:36px;height:36px;border-radius:12px;background:linear-gradient(to bottom right, ${tone.chipFrom}, ${tone.chipTo});color:#ffffff;box-shadow:0 6px 16px -6px rgba(15,23,42,0.25);">${icon}</span>
      <p style="flex:1;margin:0;font-size:14px;font-weight:600;letter-spacing:-0.01em;line-height:1.45;color:#0f172a;">${label}</p>
    </div>
  </div>
`;

// ---------- Public helpers ----------

// Success toast — emerald start-stripe + check chip
export const success = async (title) => {
  ensureStyles();
  const tone = TONES.success;
  return await Swal.fire({
    toast: true,
    position: "top",
    showConfirmButton: false,
    timerProgressBar: true,
    timer: 2800,
    background: "transparent",
    html: buildToastHtml(title || "تم الحفظ", iconCheck, tone),
    customClass: {
      popup: "khazma-swal-toast",
      icon: "swal2-icon-hidden",
      title: "swal2-title-hidden",
    },
  });
};

// Error toast — rose start-stripe + X chip
export const danger = async (title) => {
  ensureStyles();
  const tone = TONES.danger;
  return await Swal.fire({
    toast: true,
    position: "top",
    showConfirmButton: false,
    timerProgressBar: true,
    timer: 2800,
    background: "transparent",
    html: buildToastHtml(title || "حدث خطأ", iconX, tone),
    customClass: {
      popup: "khazma-swal-toast",
      icon: "swal2-icon-hidden",
      title: "swal2-title-hidden",
    },
    // showClass: {
    //   popup: "animate__animated animate__faster",
    // },
    // hideClass: {
    //   popup: "animate__animated animate__faster",
    // },
  });
};

// Confirmation dialog — refined modal with hero card
export const swal = async (
  title,
  text,
  confirmButton,
  showCancelButton = true,
) => {
  ensureStyles();

  const dialogHtml = `
    <div dir="rtl" style="position:relative;overflow:hidden;">
      <!-- Decorative orb -->
      <span aria-hidden style="pointer-events:none;position:absolute;inset-inline-end:-48px;top:-64px;width:160px;height:160px;border-radius:9999px;background:radial-gradient(closest-side,rgba(244,63,94,0.22),transparent);filter:blur(20px);"></span>

      <div style="position:relative;padding:28px 24px 8px;">
        <div style="display:flex;align-items:flex-start;gap:14px;">
          <span style="display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:48px;height:48px;border-radius:50px;background:linear-gradient(to bottom right,#f43f5e,#be123c);color:#ffffff;box-shadow:0 8px 20px -6px rgba(190,18,60,0.4), 0 0 0 1px rgba(190,18,60,0.1);">${iconExclamation}</span>
          <div style="text-align:start;min-width:0;flex:1;">
            <p style="margin:0;font-size:11px;font-weight:600;letter-spacing:0.18em;text-transform:uppercase;color:#be123c;">تأكيد الإجراء</p>
            <h3 style="margin:4px 0 0;font-size:18px;font-weight:700;letter-spacing:-0.015em;line-height:1.4;">${title || "هل أنت متأكد؟"}</h3>
            ${
              text
                ? `<p class="khazma-swal-text" style="margin:8px 0 0;font-size:14px;line-height:1.7;">${text}</p>`
                : ""
            }
          </div>
        </div>
      </div>
    </div>
  `;

  return await Swal.fire({
    html: dialogHtml,
    width: 420,
    showCancelButton,
    confirmButtonText: confirmButton || "نعم ، احذف",
    cancelButtonText: "الغاء",
    buttonsStyling: false,
    reverseButtons: false,
    customClass: {
      popup: "khazma-swal-confirm",
      htmlContainer: "swal2-html-container",
      confirmButton:
        "!inline-flex !items-center !gap-2 !rounded-full !bg-gradient-to-br !from-rose-600 !to-rose-700 !px-4 !py-2.5 !text-sm !font-semibold !text-white !shadow-sm !shadow-rose-700/25 !ring-1 !ring-rose-700/10 hover:!-translate-y-px hover:!shadow-md hover:!shadow-rose-700/30 active:!translate-y-0 !transition-all !duration-200",
      cancelButton:
        "!inline-flex !items-center !gap-2 !rounded-full !border !border-gray-100 !bg-white !px-4 !py-2.5 !text-sm !font-semibold !text-gray-700 hover:!border-gray-200 hover:!bg-gray-50 hover:!-translate-y-px active:!translate-y-0 !transition-all !duration-200 dark:!border-gray-700 dark:!bg-gray-800 dark:!text-gray-300 dark:hover:!bg-gray-700",
    },
    showClass: { popup: "animate__animated animate__faster animate__fadeIn" },
    hideClass: { popup: "animate__animated animate__faster animate__fadeOut" },
  });
};

// OTP verification dialog — refined input with letter-spacing
export const swalOtp = async () => {
  ensureStyles();

  return await Swal.fire({
    title: "التحقق من رقم الهاتف",
    iconHtml: `<span style="display:inline-flex;align-items:center;justify-content:center;width:56px;height:56px;border-radius:18px;background:linear-gradient(to bottom right,#7c3aed,#4c1d95);color:#ffffff;box-shadow:0 12px 30px -10px rgba(76,29,149,0.5), 0 0 0 1px rgba(76,29,149,0.1);">${iconShield}</span>`,
    input: "text",
    inputLabel: "رجاء ادخل رمز التحقق الذي تم ارساله الى رقم الهاتف الخاص بك",
    inputAttributes: {
      maxlength: "6",
      autocapitalize: "off",
      autocorrect: "off",
      inputmode: "numeric",
    },
    showCancelButton: false,
    confirmButtonText: "تأكيد رمز التحقق",
    buttonsStyling: false,
    customClass: {
      popup: "khazma-swal-otp",
      icon: "!border-0 !mx-auto !mt-0 !mb-4 !w-auto !h-auto !overflow-visible",
      title:
        "!text-xl !font-bold !tracking-tight !text-gray-900 dark:!text-gray-100 !mt-0 !mb-1 !p-0",
      inputLabel:
        "!text-sm !leading-relaxed !text-gray-500 dark:!text-gray-400 !mb-3 !text-center !block",
      input:
        "!h-14 !rounded-2xl !border !border-gray-200 !bg-gray-50 !text-center !text-2xl !font-bold !tabular-nums !text-gray-900 focus:!border-purple-400 focus:!bg-white focus:!ring-2 focus:!ring-purple-200 focus:!outline-none dark:!bg-gray-700 dark:!border-gray-600 dark:!text-gray-100 dark:focus:!bg-gray-900 dark:focus:!border-purple-700 dark:focus:!ring-purple-900/40 !transition-colors",
      validationMessage:
        "!bg-rose-50 !text-rose-700 !rounded-2xl !text-sm !font-medium dark:!bg-rose-950/40 dark:!text-rose-300",
      confirmButton:
        "!inline-flex !items-center !gap-2 !rounded-full !bg-gradient-to-br !from-purple-700 !to-purple-950 !px-5 !py-2.5 !text-sm !font-semibold !text-white !shadow-md !shadow-purple-900/25 !ring-1 !ring-purple-900/10 hover:!-translate-y-px hover:!shadow-lg hover:!shadow-purple-900/30 active:!translate-y-0 !transition-all !duration-200 dark:!from-purple-600 dark:!to-purple-800",
      actions: "!mt-5 !w-full !justify-center",
    },
    inputValidator: (value) => {
      if (!value || value?.length < 4) {
        return "الرجاء ادخال رمز التحقق";
      }
    },
  });
};
